import { useEffect, useRef, useState } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { Input, Spinner } from '@vnetwork/ui/primitives';
import { useDomainListPaged } from '@/api/manticore/domains';
import styles from './DomainMultiSelect.module.css';

interface DomainMultiSelectProps {
  /** Selected domain UUIDs. */
  value: string[];
  onChange: (ids: string[]) => void;
}

const PAGE_SIZE = 50;

/**
 * Searchable multi-select for CDN domains in the team permission grant flow.
 * Uses the server-side paged domain search (the same endpoint as the domain
 * list, so it scales past the per-page cap) and only queries while the panel is
 * open. Search narrows the result set; selections persist across searches. Panel
 * is in-flow (not absolutely positioned), consistent with ServiceSelect inside a
 * Dialog scroll container.
 */
export function DomainMultiSelect({ value, onChange }: DomainMultiSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  // Grow the page size to reveal more results (the domain API is truly paginated,
  // unlike services which is fixed to page 1). "Show more" bumps this.
  const [perPage, setPerPage] = useState(PAGE_SIZE);

  // Debounce so each keystroke doesn't hit the server.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  // A new search collapses back to the first page of results.
  useEffect(() => {
    setPerPage(PAGE_SIZE);
  }, [debounced]);

  const { data, isFetching } = useDomainListPaged({
    page: 1,
    perPage,
    search: debounced || undefined,
    sort: 'domain',
    order: 'asc',
    enabled: open, // only fetch while the picker is open
  });
  const rows = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  const label =
    value.length === 0
      ? 'All domains in this service'
      : `${value.length} domain${value.length !== 1 ? 's' : ''} selected`;

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTerm('');
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setTerm('');
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        data-placeholder={value.length === 0 ? '' : undefined}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.triggerLabel}>{label}</span>
        <ChevronDown size={16} className={styles.chevron} />
      </button>

      {open && (
        <div className={styles.panel}>
          <Input
            ref={inputRef}
            inputSize="sm"
            placeholder="Search domains…"
            startSlot={<Search />}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <div className={styles.list}>
            {isFetching && rows.length === 0 ? (
              <div className={styles.empty}>
                <Spinner size="sm" />
              </div>
            ) : rows.length === 0 ? (
              <div className={styles.empty}>No domains found.</div>
            ) : (
              rows.map((d) => {
                const id = d.id ?? d.domain;
                const checked = value.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    className={checked ? `${styles.item} ${styles.itemSelected}` : styles.item}
                  >
                    <Check
                      size={14}
                      className={checked ? styles.check : `${styles.check} ${styles.checkHidden}`}
                    />
                    <span className={styles.itemTitle}>{d.domain}</span>
                  </button>
                );
              })
            )}
          </div>

          {rows.length < total && (
            <button
              type="button"
              className={styles.loadMore}
              disabled={isFetching}
              onClick={() => setPerPage((p) => p + PAGE_SIZE)}
            >
              {isFetching ? 'Loading…' : `Show more (${rows.length} of ${total})`}
            </button>
          )}

          <div className={styles.footer}>
            <span>
              {value.length > 0
                ? `${value.length} selected`
                : `${total} domain${total !== 1 ? 's' : ''}`}
            </span>
            {value.length > 0 && (
              <button type="button" className={styles.clearBtn} onClick={() => onChange([])}>
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
